import React from 'react';
import { StepProps } from './types';

const Step4Overview: React.FC<StepProps> = ({ form, setForm, handleChange, errors }) => {
    return (
        <div className="space-y-10">
            {/* Header Section */}
            <div className="flex items-center gap-3 mb-5 px-1">
                <div className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-lg shadow-md">
                    4
                </div>
                <div>
                    <h5 className="text-xl font-bold text-gray-800">Project Overview</h5>
                    <p className="text-xs text-gray-500">Provide an overview of the project details.</p>
                </div>
            </div>

            {/* Project Overview Details */}
            <div className="space-y-4">
                <h6 className="text-sm font-bold text-gray-800">Project Overview Details</h6>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                    {/* Configuration */}
                    <div className="flex items-center bg-white border border-gray-100 rounded-xl p-3 gap-3 transition-all focus-within:border-blue-300">
                        <div className="w-10 h-10 rounded-xl bg-fuchsia-50 flex items-center justify-center shrink-0">
                            <i className="fa-solid fa-city text-fuchsia-600 text-[18px]"></i>
                        </div>
                        <div className="flex-1 min-w-0">
                            <label className="text-[10px] font-bold text-[#1e1b4b] block mb-0.5">
                                Configuration <span className="text-red-500">*</span>
                            </label>
                            <input 
                                type="text" 
                                name="overviewConfiguration" 
                                value={form.overviewConfiguration || ''} 
                                onChange={handleChange} 
                                className="w-full text-[13px] font-extrabold text-[#1e1b4b] bg-transparent outline-none truncate" 
                                placeholder="2, 3 BHK" 
                            />
                        </div>
                    </div>

                    {/* Total Amenities */}
                    <div className="flex items-center bg-white border border-gray-100 rounded-xl p-3 gap-3 transition-all focus-within:border-blue-300">
                        <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center shrink-0">
                            <i className="fa-solid fa-arrow-trend-up text-emerald-500 text-[18px]"></i>
                        </div>
                        <div className="flex-1 min-w-0">
                            <label className="text-[10px] font-bold text-[#1e1b4b] block mb-0.5">
                                Total Amenities <span className="text-red-500">*</span>
                            </label>
                            <input 
                                type="text" 
                                name="overviewAmenitiesIcon" 
                                value={form.overviewAmenitiesIcon || ''} 
                                onChange={handleChange} 
                                className="w-full text-[13px] font-extrabold text-[#1e1b4b] bg-transparent outline-none truncate" 
                                placeholder="30+ Premium Amenities" 
                            />
                        </div>
                    </div>

                    {/* Total Plot Area */}
                    <div className="flex items-center bg-white border border-gray-100 rounded-xl p-3 gap-3 transition-all focus-within:border-blue-300">
                        <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center shrink-0">
                            <i className="fa-solid fa-building text-orange-500 text-[18px]"></i>
                        </div>
                        <div className="flex-1 min-w-0">
                            <label className="text-[10px] font-bold text-[#1e1b4b] block mb-0.5">
                                Total Plot Area <span className="text-red-500">*</span>
                            </label>
                            <input 
                                type="text" 
                                name="overviewTotalPlotArea" 
                                value={form.overviewTotalPlotArea || ''} 
                                onChange={handleChange} 
                                className="w-full text-[13px] font-extrabold text-[#1e1b4b] bg-transparent outline-none truncate" 
                                placeholder="5.5 Acres" 
                            />
                        </div>
                    </div>

                    {/* Avg. Price / Sq.Ft. */}
                    <div className="flex items-center bg-white border border-gray-100 rounded-xl p-3 gap-3 transition-all focus-within:border-blue-300">
                        <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center shrink-0">
                            <i className="fa-solid fa-ticket text-blue-500 text-[18px]"></i>
                        </div>
                        <div className="flex-1 min-w-0">
                            <label className="text-[10px] font-bold text-[#1e1b4b] block mb-0.5">
                                Avg. Price / Sq.Ft. <span className="text-red-500">*</span>
                            </label>
                            <input 
                                type="text" 
                                name="overviewAvgPricePerSqFt" 
                                value={form.overviewAvgPricePerSqFt || ''} 
                                onChange={handleChange} 
                                className="w-full text-[13px] font-extrabold text-[#1e1b4b] bg-transparent outline-none truncate" 
                                placeholder="₹ 5,500" 
                            />
                        </div>
                    </div>

                    {/* Open Space % */}
                    <div className="flex items-center bg-white border border-gray-100 rounded-xl p-3 gap-3 transition-all focus-within:border-blue-300">
                        <div className="w-10 h-10 rounded-xl bg-rose-50 flex items-center justify-center shrink-0">
                            <i className="fa-solid fa-chart-line text-rose-500 text-[18px]"></i>
                        </div>
                        <div className="flex-1 min-w-0">
                            <label className="text-[10px] font-bold text-[#1e1b4b] block mb-0.5">
                                Open Space % <span className="text-red-500">*</span>
                            </label>
                            <input 
                                type="text" 
                                name="overviewOpenSpacePercentage" 
                                value={form.overviewOpenSpacePercentage || ''} 
                                onChange={handleChange} 
                                className="w-full text-[13px] font-extrabold text-[#1e1b4b] bg-transparent outline-none truncate" 
                                placeholder="65%" 
                            />
                        </div>
                    </div>

                    {/* Bathrooms */}
                    <div className="flex items-center bg-white border border-gray-100 rounded-xl p-3 gap-3 transition-all focus-within:border-blue-300">
                        <div className="w-10 h-10 rounded-xl bg-teal-50 flex items-center justify-center shrink-0">
                            <i className="fa-solid fa-bath text-teal-500 text-[18px]"></i>
                        </div>
                        <div className="flex-1 min-w-0">
                            <label className="text-[10px] font-bold text-[#1e1b4b] block mb-0.5">
                                Bathrooms <span className="text-red-500">*</span>
                            </label>
                            <input 
                                type="text" 
                                name="overviewBathrooms" 
                                value={form.overviewBathrooms || ''} 
                                onChange={handleChange} 
                                className="w-full text-[13px] font-extrabold text-[#1e1b4b] bg-transparent outline-none truncate" 
                                placeholder="3" 
                            />
                        </div>
                    </div>

                    {/* Age of Construction */}
                    <div className="flex items-center bg-white border border-gray-100 rounded-xl p-3 gap-3 transition-all focus-within:border-blue-300">
                        <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center shrink-0">
                            <i className="fa-regular fa-calendar text-blue-600 text-[18px]"></i>
                        </div>
                        <div className="flex-1 min-w-0">
                            <label className="text-[10px] font-bold text-[#1e1b4b] block mb-0.5">
                                Age of Construction <span className="text-red-500">*</span>
                            </label>
                            <input 
                                type="text" 
                                name="overviewAgeOfConstruction" 
                                value={form.overviewAgeOfConstruction || ''} 
                                onChange={handleChange} 
                                className="w-full text-[13px] font-extrabold text-[#1e1b4b] bg-transparent outline-none truncate" 
                                placeholder="0 - 5 Years" 
                            />
                        </div>
                    </div>

                    {/* Alloted Parking */}
                    <div className="flex items-center bg-white border border-gray-100 rounded-xl p-3 gap-3 transition-all focus-within:border-blue-300">
                        <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center shrink-0">
                            <i className="fa-solid fa-car text-purple-600 text-[18px]"></i>
                        </div>
                        <div className="flex-1 min-w-0">
                            <label className="text-[10px] font-bold text-[#1e1b4b] block mb-0.5">
                                Alloted Parking <span className="text-red-500">*</span>
                            </label>
                            <input 
                                type="text" 
                                name="overviewParking" 
                                value={form.overviewParking || ''} 
                                onChange={handleChange} 
                                className="w-full text-[13px] font-extrabold text-[#1e1b4b] bg-transparent outline-none truncate" 
                                placeholder="1 Covered, 1 Open" 
                            />
                        </div>
                    </div>
                    {/* Completion Progress Bar */}
                    <div className="flex items-center bg-white border border-gray-100 rounded-xl p-3 gap-3 transition-all focus-within:border-blue-300">
                        <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center shrink-0">
                            <i className="fa-solid fa-chart-bar text-orange-500 text-[18px]"></i>
                        </div>
                        <div className="flex-1 min-w-0 flex flex-col justify-center">
                            <div className="flex justify-between items-center mb-1">
                                <label className="text-[10px] font-bold text-[#1e1b4b]">
                                    Completion Progress <span className="text-red-500">*</span>
                                </label>
                                <span className="text-[10px] font-black text-blue-600">%</span>
                            </div>
                            <div className="flex items-center gap-3">
                                <input 
                                    type="number" 
                                    min="0" 
                                    max="100"
                                    name="progressPercentage" 
                                    value={form.progressPercentage || 0} 
                                    onChange={handleChange} 
                                    className="w-10 text-[13px] font-extrabold text-[#1e1b4b] bg-transparent outline-none" 
                                />
                                <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                    <div className="h-full bg-blue-600 rounded-full" style={{ width: `${form.progressPercentage || 0}%` }}></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Project Overview Description */}
            <div className="space-y-4">
                <h6 className="text-sm font-bold text-gray-800">Project Overview Description</h6>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div>
                        <div className="flex justify-between mb-1">
                            <label className="text-[10px] font-bold text-black">Short Description *</label>
                        </div>
                        <p className="text-[9px] text-gray-500 mb-2">Enter a short summary of the project (max 250 characters).</p>
                        <div className="bg-white border border-gray-100 rounded-2xl shadow-[0_2px_10px_-3px_rgba(6,81,237,0.1)] focus-within:border-blue-300 transition-all p-1">
                            <textarea 
                                name="shortDescription" 
                                value={form.shortDescription || ''} 
                                onChange={handleChange} 
                                className="w-full h-32 px-4 py-3 bg-transparent outline-none text-[11px] font-medium text-gray-800 resize-none" 
                                placeholder="A premium residential project offering modern living with world-class amenities and excellent connectivity."
                                maxLength={250}
                            ></textarea>
                            <div className="text-right px-4 pb-2 text-[9px] font-bold text-gray-400">
                                {form.shortDescription?.length || 0} / 250
                            </div>
                        </div>
                    </div>
                    <div>
                        <div className="flex justify-between mb-1">
                            <label className="text-[10px] font-bold text-black">Long Description *</label>
                        </div>
                        <p className="text-[9px] text-gray-500 mb-2">Enter detailed information about the project.</p>
                        <div className="bg-white border border-gray-100 rounded-2xl shadow-[0_2px_10px_-3px_rgba(6,81,237,0.1)] focus-within:border-blue-300 transition-all p-1 flex">
                            <div className="flex-1">
                                <textarea 
                                    name="fullDescription" 
                                    value={form.fullDescription || ''} 
                                    onChange={handleChange} 
                                    className="w-full h-32 px-4 py-3 bg-transparent outline-none text-[11px] font-medium text-gray-800 resize-none custom-scrollbar" 
                                    placeholder="A thoughtfully planned residential development spread across 5.5 acres..."
                                    maxLength={2000}
                                ></textarea>
                                <div className="text-right px-4 pb-2 text-[9px] font-bold text-gray-400">
                                    {form.fullDescription?.length || 0} / 2000
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>



            {/* Overview Summary (Auto Preview) */}
            <div className="bg-green-50/30 border border-green-100 rounded-3xl p-6">
                <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-lg shadow-md">
                        4
                    </div>
                    <div>
                        <h5 className="text-xl font-bold text-gray-800">Project Overview</h5>
                        <p className="text-xs text-gray-500">Overview Summary (Auto Preview)</p>
                    </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                    <div className="bg-white border border-gray-100 rounded-xl p-3 flex items-center gap-3">
                        <i className="fa-solid fa-city text-fuchsia-600 text-lg shrink-0"></i>
                        <div className="min-w-0">
                            <span className="block text-[8px] font-bold text-gray-400">Configuration</span>
                            <span className="block text-[10px] font-black text-gray-800 truncate">{form.overviewConfiguration || '-'}</span>
                        </div>
                    </div>
                    <div className="bg-white border border-gray-100 rounded-xl p-3 flex items-center gap-3">
                        <i className="fa-solid fa-arrow-trend-up text-emerald-500 text-lg shrink-0"></i>
                        <div className="min-w-0">
                            <span className="block text-[8px] font-bold text-gray-400">Total Amenities</span>
                            <span className="block text-[10px] font-black text-gray-800 truncate">{form.overviewAmenitiesIcon || '-'}</span>
                        </div>
                    </div>
                    <div className="bg-white border border-gray-100 rounded-xl p-3 flex items-center gap-3">
                        <i className="fa-solid fa-building text-orange-500 text-lg shrink-0"></i>
                        <div className="min-w-0">
                            <span className="block text-[8px] font-bold text-gray-400">Total Plot Area</span>
                            <span className="block text-[10px] font-black text-gray-800 truncate">{form.overviewTotalPlotArea || '-'}</span>
                        </div>
                    </div>
                    <div className="bg-white border border-gray-100 rounded-xl p-3 flex items-center gap-3">
                        <i className="fa-solid fa-ticket text-blue-500 text-lg shrink-0"></i>
                        <div className="min-w-0">
                            <span className="block text-[8px] font-bold text-gray-400">Avg. Price / Sq.Ft.</span>
                            <span className="block text-[10px] font-black text-gray-800 truncate">{form.overviewAvgPricePerSqFt || '-'}</span>
                        </div>
                    </div>
                    <div className="bg-white border border-gray-100 rounded-xl p-3 flex items-center gap-3">
                        <i className="fa-solid fa-chart-line text-rose-500 text-lg shrink-0"></i>
                        <div className="min-w-0">
                            <span className="block text-[8px] font-bold text-gray-400">Open Space %</span>
                            <span className="block text-[10px] font-black text-gray-800 truncate">{form.overviewOpenSpacePercentage || '-'}</span>
                        </div>
                    </div>
                    <div className="bg-white border border-gray-100 rounded-xl p-3 flex items-center gap-3">
                        <i className="fa-solid fa-bath text-teal-500 text-lg shrink-0"></i>
                        <div className="min-w-0">
                            <span className="block text-[8px] font-bold text-gray-400">Bathrooms</span>
                            <span className="block text-[10px] font-black text-gray-800 truncate">{form.overviewBathrooms || '-'}</span>
                        </div>
                    </div>
                    <div className="bg-white border border-gray-100 rounded-xl p-3 flex items-center gap-3">
                        <i className="fa-regular fa-calendar text-blue-600 text-lg shrink-0"></i>
                        <div className="min-w-0">
                            <span className="block text-[8px] font-bold text-gray-400">Age of Construction</span>
                            <span className="block text-[10px] font-black text-gray-800 truncate">{form.overviewAgeOfConstruction || '-'}</span>
                        </div>
                    </div>
                    <div className="bg-white border border-gray-100 rounded-xl p-3 flex items-center gap-3">
                        <i className="fa-solid fa-car text-purple-600 text-lg shrink-0"></i>
                        <div className="min-w-0">
                            <span className="block text-[8px] font-bold text-gray-400">Alloted Parking</span>
                            <span className="block text-[10px] font-black text-gray-800 truncate">{form.overviewParking || '-'}</span>
                        </div>
                    </div>
                    
                    <div className="bg-white border border-gray-100 rounded-xl p-3 flex items-center gap-3 col-span-2">
                        <i className="fa-solid fa-chart-bar text-orange-500 text-lg shrink-0"></i>
                        <div className="flex-1 min-w-0 flex flex-col justify-center">
                            <div className="flex justify-between items-center mb-1">
                                <span className="block text-[8px] font-bold text-gray-400">Completion Progress</span>
                                <span className="text-[8px] font-black text-blue-600">%</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="text-[10px] font-black text-gray-800 w-8">{form.progressPercentage || 0}%</span>
                                <div className="flex-1 h-1 bg-gray-100 rounded-full overflow-hidden">
                                    <div className="h-full bg-blue-600 rounded-full" style={{ width: `${form.progressPercentage || 0}%` }}></div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white border border-gray-100 rounded-xl p-3 flex items-center gap-3">
                        <i className="fa-regular fa-building text-blue-600 text-lg shrink-0"></i>
                        <div className="min-w-0">
                            <span className="block text-[8px] font-bold text-gray-400">Towers / Blocks</span>
                            <span className="block text-[10px] font-black text-gray-800 truncate">{form.overviewTotalTowers || '-'}</span>
                        </div>
                    </div>
                    <div className="bg-white border border-gray-100 rounded-xl p-3 flex items-center gap-3">
                        <i className="fa-solid fa-house-chimney-window text-blue-600 text-lg shrink-0"></i>
                        <div className="min-w-0">
                            <span className="block text-[8px] font-bold text-gray-400">Total Floors</span>
                            <span className="block text-[10px] font-black text-gray-800 truncate">{form.overviewTotalFloors || '-'}</span>
                        </div>
                    </div>
                    <div className="bg-white border border-gray-100 rounded-xl p-3 flex items-center gap-3">
                        <i className="fa-solid fa-house-chimney-window text-blue-600 text-lg shrink-0"></i>
                        <div className="min-w-0">
                            <span className="block text-[8px] font-bold text-gray-400">Sub-G + Floors</span>
                            <span className="block text-[10px] font-black text-gray-800 truncate">{form.overviewFloors || '-'}</span>
                        </div>
                    </div>
                    <div className="bg-white border border-gray-100 rounded-xl p-3 flex items-center gap-3">
                        <i className="fa-solid fa-building-user text-fuchsia-600 text-lg shrink-0"></i>
                        <div className="min-w-0">
                            <span className="block text-[8px] font-bold text-gray-400">Total Units</span>
                            <span className="block text-[10px] font-black text-gray-800 truncate">{form.overviewTotalUnits || '-'}</span>
                        </div>
                    </div>
                </div>
            </div>


        </div>
    );
};

export default Step4Overview;
